import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { X } from 'lucide-react'
import { useDashboardStore } from '../../store/dashboardStore'
import { useT } from '../../hooks/useTranslation'
import BotCard from './BotCard'
import AddBotModal from './AddBotModal'
import { getBots, getConfig, saveConfig } from '../../services/api'
import { botApi } from '../../services/botApi'
// Слоты ботов «тип счёта × биржа» (см. шапку src/utils/botSlots.js).
import { botAccountType, freeExchanges as freeExchangesFor, bookableExchanges } from '../../utils/botSlots'

const MAX_PAIR_LENGTH = 20

// ─── Кнопка "Добавить бота" ──────────────────────────────────────────────────
function AddBotButton({ t, canAdd, hasApiKeys, atLimit, noOpenExchanges = false, onClick, label, compact = false, disabled = false, pulsing = false }) {
  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    if (!showTooltip) return
    const timer = setTimeout(() => setShowTooltip(false), 2500)
    return () => clearTimeout(timer)
  }, [showTooltip])

  const handleClick = () => {
    if (disabled) return
    if (canAdd) {
      onClick()
    } else {
      setShowTooltip(v => !v)
    }
  }

  // Причина недоступности кнопки. 'noExchanges' — админ закрыл все биржи
  // (скрыл или перевёл «в разработке», 2026-10-26): тогда «боты уже на всех
  // биржах» было бы неправдой, и юзер искал бы несуществующих ботов.
  const reason = !hasApiKeys ? 'noKeys' : noOpenExchanges ? 'noExchanges' : atLimit ? 'atLimit' : null

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleClick}
        data-hint="add-bot"
        // Пульсация (П.3 task.txt): привлекает внимание, когда юзер может
        // создать пробного бота, но ещё не сделал это. Класс применяется
        // только к АКТИВНОЙ зелёной кнопке (canAdd && !disabled).
        className={pulsing && canAdd && !disabled ? 'add-bot-pulse' : undefined}
        style={{
          padding: compact ? '9px 18px' : '13px 32px',
          borderRadius: compact ? 10 : 14,
          background: canAdd && !disabled
            ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
            : 'rgba(255,255,255,0.06)',
          color: canAdd && !disabled ? '#fff' : '#636366',
          fontSize: compact ? 13 : 15, fontWeight: 700,
          border: 'none',
          cursor: canAdd && !disabled ? 'pointer' : 'not-allowed',
          WebkitTapHighlightColor: 'transparent',
          opacity: canAdd && !disabled ? 1 : 0.5,
        }}
      >
        {label || t.botsAddBot}
      </button>

      {reason && showTooltip && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
          transform: 'translateX(-50%)',
          background: '#1C1C1E', border: '1px solid rgba(255,107,53,0.25)',
          borderRadius: 10, padding: '10px 14px', zIndex: 100,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          width: reason === 'noKeys' ? 260 : 240, textAlign: 'center',
        }}>
          <p style={{ fontSize: 12, color: '#8E8E93', margin: 0, lineHeight: 1.5 }}>
            {reason === 'noKeys'
              ? t.noApiKeysHint
              : reason === 'noExchanges'
                ? `🔒 ${t.botsExchangeUnavailable}`
                : `⚠️ ${t.botsAllCreated}`}
          </p>
        </div>
      )}
    </div>
  )
}

// ─── Active Bots tab ──────────────────────────────────────────────────────────
// Тип счёта бота (botAccountType) и слоты «тип счёта × биржа» (freeExchanges)
// берутся из общего модуля src/utils/botSlots.js — чтобы страница «Боты», форма
// создания и раздел «Биржи» считали доступность одинаково: демо-бот на Bybit НЕ
// занимает демо-слот на Binance (правило 2026-09-20).

function ActiveBotsTab({ t }) {
  const userBots        = useDashboardStore(s => s.userBots)
  const setUserBots     = useDashboardStore(s => s.setUserBots)
  const hasApiKeysState = useDashboardStore(s => s.hasApiKeys)
  const hasRealKeys     = useDashboardStore(s => s.hasRealKeys)
  const hasDemoKeys     = useDashboardStore(s => s.hasDemoKeys)
  const demoAllowed     = useDashboardStore(s => s.demoAllowed)
  const realAllowed     = useDashboardStore(s => s.realAllowed)
  const virtualOrdersUsed  = useDashboardStore(s => s.virtualOrdersUsed)
  const virtualOrdersLimit = useDashboardStore(s => s.virtualOrdersLimit)
  const setActiveModal     = useDashboardStore(s => s.setActiveModal)
  // П.3 (task.txt): подсказка уже рассказала про пробного бота — кнопка
  // «Добавить бота» начинает пульсировать, чтобы юзер видел, куда нажимать.
  const hintsIntroSeen = useDashboardStore(s => s.hintsIntroSeen)
  const hintsDone      = useDashboardStore(s => s.hintsDone)
  const isAdmin        = useDashboardStore(s => s.isAdmin)
  // Публичный конфиг админки: из него берём режимы бирж (вкл/«в разработке»/скрыта).
  const appConfig      = useDashboardStore(s => s.appConfig)
  const [showAddBot, setShowAddBot] = useState(null) // { type: 'demo'|'real' } | null
  const [loadError, setLoadError]   = useState(false)
  const [creatingVirtual, setCreatingVirtual] = useState(false)
  // Флаг: сервер уже подтвердил актуальный список ботов.
  // Пока false — доверять userBots.length нельзя (могут быть устаревшие/пустые данные).
  const [botsLoaded, setBotsLoaded] = useState(false)
  const count   = userBots.length

  useEffect(() => {
    let cancelled = false
    setBotsLoaded(false)
    getBots()
      .then(bots => {
        if (cancelled) return
        setUserBots((Array.isArray(bots) ? bots : []).map(b => ({ ...b, accountMode: botAccountType(b) })))
        setLoadError(false)
      })
      .catch(() => {
        if (cancelled) return
        setLoadError(true)
      })
      .finally(() => {
        if (cancelled) return
        setBotsLoaded(true)
      })
    return () => { cancelled = true }
  }, [])

  // Доступные по подписке типы счёта: демо — при любой активной подписке,
  // реальный — только при реальной. Слот бота — пара (тип счёта × биржа): на
  // каждом типе у юзера может быть по одному боту НА КАЖДОЙ бирже, поэтому
  // «тип исчерпан» = на всех биржах этого типа боты уже есть. Раньше здесь
  // считалось «есть хоть один бот этого типа» — из-за этого демо-бот на Bybit
  // блокировал всю кнопку «Добавить бота», хотя на Binance демо-слот был пуст.
  const allowedTypes = [
    ...(demoAllowed ? ['demo'] : []),
    ...(realAllowed ? ['real'] : []),
  ]
  // Свободные слоты по типам счёта: { demo: ['binance','bitget'], real: [...] }.
  // Считаем только по биржам, ОТКРЫТЫМ админом (2026-10-26): биржа «в
  // разработке» или скрытая не считается свободным слотом — создать там бота
  // всё равно нельзя, кнопка «Добавить бота» не должна выглядеть доступной,
  // если других слотов нет (см. bookableExchanges в botSlots.js).
  const bookableIds = bookableExchanges(appConfig)
  const freeByType = {}
  for (const type of allowedTypes) freeByType[type] = freeExchangesFor(userBots, type, bookableIds)
  // Типы, по которым ещё МОЖНО создать бота (подписка открывает И есть
  // свободная биржа хотя бы на одной из них).
  const availableTypes = allowedTypes.filter(type => freeByType[type].length > 0)
  const totalFreeSlots = allowedTypes.reduce((n, type) => n + freeByType[type].length, 0)

  // Есть ли у пользователя хотя бы один сохранённый API-ключ (реального ИЛИ
  // демо-счёта) — от этого зависит кликабельность кнопки «Добавить бота».
  const hasAnyKeys = hasApiKeysState || hasRealKeys || hasDemoKeys

  // ── Пробный виртуальный бот (Волна 1) ─────────────────────────────────────
  // Новый юзер: нет API-ключей и подписка не открыла ни одного типа счёта
  // (allowedTypes пуст). Для него «Добавить бота» создаёт ПРОБНОГО
  // виртуального бота СРАЗУ, без модалки и без ключей.
  const virtualBot = userBots.find(b => b.virtual)
  const isFreeNewUser = !hasAnyKeys && allowedTypes.length === 0
  const canCreateVirtual = isFreeNewUser && !virtualBot && virtualOrdersUsed < virtualOrdersLimit

  // Кнопка «Добавить бота» видна ВСЕГДА. Активна, когда сервер подтвердил
  // список и (а) сохранён API-ключ + есть свободный слот (тип счёта × биржа),
  // либо (б) юзер новый и может получить пробного виртуального бота.
  const canAddBot = botsLoaded && (canCreateVirtual || (hasAnyKeys && totalFreeSlots > 0))

  // П.3: пульс — только для активной зелёной кнопки нового юзера, которому
  // подсказка уже объяснила про пробного бота (карточка-приветствие показана
  // ИЛИ тур завершён). Админу и юзеру с ключами — не пульсируем.
  const pulseAddBot = !isAdmin && (hintsIntroSeen || hintsDone) && canCreateVirtual && canAddBot

  // Авто-создание пробного бота одним тапом (без AddBotModal). Ответ бэкенда:
  // { bot: {...} } — как у обычного POST /api/bots.
  const handleAddClick = async () => {
    if (canCreateVirtual) {
      setCreatingVirtual(true)
      try {
        const res = await botApi.createVirtualBot()
        const bot = res?.bot
        if (bot) {
          // Повторное «Добавить бота» с уже существующим пробным ботом
          // (п.5 task.txt) бэкенд отвечает этим же ботом — не дублируем карточку.
          setUserBots(prev =>
            prev.some(b => String(b.id) === String(bot.id))
              ? prev
              : [...prev, { ...bot, accountMode: botAccountType(bot) }]
          )
          window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light')
        } else {
          // Ответ пришёл, но без бота (неожиданная форма) — не молчим, иначе
          // «Добавить бота» выглядело бы как ничего не произошло.
          console.error('[BotsPage] Unexpected createVirtualBot response:', res)
          throw new Error(`Unexpected server response: ${JSON.stringify(res ?? null)}`)
        }
      } catch (err) {
        console.error('[BotsPage] Virtual bot creation failed:', err)
        const tg = window.Telegram?.WebApp
        const m = String(err?.message || '').match(/"detail"\s*:\s*"([^"]+)"/)
        const message = m ? m[1] : err?.message
        if (message) { if (tg?.showAlert) tg.showAlert(`⚠️ ${message}`); else alert(`⚠️ ${message}`) }
      } finally {
        setCreatingVirtual(false)
      }
    } else {
      setShowAddBot({ type: availableTypes[0] })
    }
  }

  const addBotButton = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <AddBotButton
        t={t}
        canAdd={canAddBot}
        hasApiKeys={hasAnyKeys}
        atLimit={hasAnyKeys && totalFreeSlots === 0}
        noOpenExchanges={hasAnyKeys && bookableIds.length === 0}
        onClick={handleAddClick}
        disabled={creatingVirtual}
        label={creatingVirtual ? '...' : undefined}
        pulsing={pulseAddBot}
      />
      {/* Подсказка для нового юзера: бесплатный пробный бот без ключей */}
      {canCreateVirtual && (
        <span style={{
          fontSize: 12, color: '#636366',
          maxWidth: 260, textAlign: 'center', lineHeight: 1.5,
        }}>
          {t.virtualCreateHint}
        </span>
      )}
    </div>
  )

  // Плашка оплаты: пробный лимит (2 виртуальные сделки) исчерпан, а активной
  // подписки (типов счёта) у юзера нет — кнопка «Добавить бота» уже неактивна,
  // предлагаем оформить подписку. У подписчика плашка не нужна — он и так
  // может создавать обычных ботов.
  const trialFinished = virtualOrdersUsed >= virtualOrdersLimit && !demoAllowed && !realAllowed
  const paymentWall = trialFinished ? (
    <div style={{
      background: 'linear-gradient(135deg, rgba(255,107,53,0.10) 0%, rgba(255,59,48,0.06) 100%)',
      border: '1px solid rgba(255,107,53,0.25)',
      borderRadius: 16, padding: '16px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 26 }}>🔒</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{t.virtualTrialFinishedTitle}</div>
      <div style={{ fontSize: 12.5, color: '#8E8E93', lineHeight: 1.5, maxWidth: 280 }}>
        {t.virtualTrialFinishedDesc}
      </div>
      <button
        onClick={() => { window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light'); setActiveModal('subscription') }}
        style={{
          marginTop: 6, padding: '12px 26px', borderRadius: 12,
          fontSize: 14, fontWeight: 700,
          background: 'linear-gradient(135deg, #FF6B35 0%, #FF3B30 100%)',
          color: '#fff', border: 'none', cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {t.virtualSubscribeBtn}
      </button>
    </div>
  ) : null

  return (
    <div>
      {loadError && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 12,
          background: 'rgba(255,59,48,0.1)', border: '1px solid rgba(255,59,48,0.2)',
          fontSize: 12, color: '#FF3B30', textAlign: 'center',
        }}>
          {t.botsLoadError}
        </div>
      )}
      {paymentWall}
      {count === 0 ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', textAlign: 'center',
          minHeight: 'calc(100dvh - 320px)', gap: 10,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 20, fontSize: 34,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,107,53,0.12)',
            border: '1px solid rgba(255,107,53,0.1)',
            marginBottom: 4,
          }}>
            🤖
          </div>
          <p style={{ fontSize: 17, fontWeight: 700, color: '#fff', margin: 0 }}>
            {t.botsNoBots}
          </p>
          <p style={{ fontSize: 13, color: '#636366', margin: 0, maxWidth: 220, lineHeight: 1.5 }}>
            {t.botsNoBotsHint}
          </p>

          <div style={{ marginTop: 8 }}>
            {/* Пока не пришло подтверждение с сервера — кнопку не показываем вовсе,
                чтобы исключить клик в "окно гонки" */}
            {!botsLoaded ? (
              <div style={{ fontSize: 12, color: '#636366' }}>{t.loading}</div>
            ) : (
              addBotButton
            )}
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {userBots.map(bot => (
              <BotCard key={bot.id} bot={bot} />
            ))}
          </div>
          {/* Кнопка «Добавить бота» — всегда под списком: активна, если ключи
              сохранены и есть тип счёта, на котором бота ещё нет (например,
              создали демо-бота → доступна кнопка для реального счёта). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16, alignItems: 'center' }}>
            {addBotButton}
          </div>
        </>
      )}

      {showAddBot && (
        <AddBotModal
          defaultType="futures"
          defaultAccountMode={availableTypes.length === 1 ? availableTypes[0] : undefined}
          onClose={() => setShowAddBot(null)}
        />
      )}
    </div>
  )
}

// ─── Configuration tab ────────────────────────────────────────────────────────

function normalizePair(raw) {
  return raw
    .trim()
    .toUpperCase()
    .replace(/[\s/_-]/g, '')
    .slice(0, MAX_PAIR_LENGTH)
}

const PairBlacklistInput = forwardRef(function PairBlacklistInput({ value, onChange, t, onSave }, ref) {
  const [input, setInput] = useState('')

  const add = (pair) => {
    const normalized = normalizePair(pair)
    if (normalized && !value.includes(normalized)) {
      const newValue = [...value, normalized]
      onChange(newValue)
      if (onSave) onSave(newValue)
    }
    setInput('')
  }

  const remove = (pair) => {
    const newValue = value.filter(p => p !== pair)
    onChange(newValue)
    if (onSave) onSave(newValue)
  }

  useImperativeHandle(ref, () => ({
    commitPending: () => {
      const normalized = normalizePair(input)
      setInput('')
      if (normalized && !value.includes(normalized)) {
        onChange([...value, normalized])
        return normalized
      }
      return null
    },
  }), [input, value, onChange])

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault()
      add(input)
    }
  }

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {value.map(pair => (
            <div key={pair} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '5px 10px', borderRadius: 8,
              background: 'rgba(255,59,48,0.1)',
              border: '1px solid rgba(255,59,48,0.22)',
            }}>
              <span style={{ fontSize: 12, color: '#FF3B30', fontWeight: 600 }}>{pair}</span>
              <button
                type="button"
                onClick={() => remove(pair)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 1px', display: 'flex', lineHeight: 1 }}
              >
                <X size={10} color="#FF3B30" />
              </button>
            </div>
          ))}
        </div>
      )}
      <input
        type="text"
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        maxLength={MAX_PAIR_LENGTH}
        placeholder={t.addBotBlacklistPh}
        style={{
          width: '100%', padding: '12px 14px', borderRadius: 12,
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
          color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  )
})

function ConfigTab({ t }) {
  const storedConfig  = useDashboardStore(s => s.botsConfig)
  const setBotsConfig = useDashboardStore(s => s.setBotsConfig)
  const hasApiKeysState = useDashboardStore(s => s.hasApiKeys)
  const [cfg, setCfg]     = useState({ blacklistedPairs: [], ...storedConfig })
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const blacklistInputRef = useRef(null)

  // Загружаем конфиг с бэкенда при открытии вкладки
  useEffect(() => {
    getConfig().then(remote => {
      if (remote) {
        setBotsConfig(remote)
        setCfg(prev => ({ ...prev, ...remote }))
      }
    }).catch(err => {
      console.error('Failed to load config:', err)
    })
  }, [])

  // Синхронизируем форму при изменении конфигурации в сторе
  useEffect(() => {
    if (storedConfig && typeof storedConfig === 'object') {
      setCfg(prev => ({ ...prev, ...storedConfig }))
    }
  }, [storedConfig])

  useEffect(() => {
    if (!showTooltip) return
    const timer = setTimeout(() => setShowTooltip(false), 2500)
    return () => clearTimeout(timer)
  }, [showTooltip])

  const update = (key, value) => setCfg(prev => ({ ...prev, [key]: value }))

  const handleSave = async () => {
    if (!hasApiKeysState || saving) return
    const tg = window?.Telegram?.WebApp

    const pending = blacklistInputRef.current?.commitPending?.()
    const pairsToSave = pending
      ? [...(cfg.blacklistedPairs ?? []), pending]
      : (cfg.blacklistedPairs ?? [])

    setSaving(true)
    try {
      const result = await saveConfig({
        blacklistedPairs: pairsToSave,
      })

      const savedPairs = result?.blacklistedPairs ?? pairsToSave
      setCfg(prev => ({ ...prev, blacklistedPairs: savedPairs }))
      setBotsConfig({ ...storedConfig, ...cfg, blacklistedPairs: savedPairs })

      setSaved(true)
      setTimeout(() => setSaved(false), 1800)
      tg?.HapticFeedback?.impactOccurred?.('light')
    } catch (err) {
      console.error('Save config error:', err)
      const message = err?.message ?? t.configSaveError
      if (tg?.showAlert) {
        tg.showAlert(`❌ ${t.alertErrorPrefix}: ${message}`)
      } else {
        alert(`❌ ${t.alertErrorPrefix}: ${message}`)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleButtonClick = () => {
    if (hasApiKeysState) {
      handleSave()
    } else {
      setShowTooltip(v => !v)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Blacklist */}
      <div data-hint="config-blacklist" style={{
        background: '#1A1A1A', borderRadius: 16,
        padding: 16, border: '1px solid rgba(255,255,255,0.05)',
      }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>
          {t.addBotBlacklistTitle}
        </p>
        <p style={{ fontSize: 12, color: '#636366', margin: '0 0 14px', lineHeight: 1.45 }}>
          {t.addBotBlacklistDesc}
        </p>
        <PairBlacklistInput
          ref={blacklistInputRef}
          value={cfg.blacklistedPairs ?? []}
          onChange={val => update('blacklistedPairs', val)}
          t={t}
          onSave={async (newPairs) => {
            if (!hasApiKeysState) {
              setShowTooltip(true)
              // Откатываем UI, если API-ключи не подключены
              setCfg(prev => ({ ...prev, blacklistedPairs: storedConfig.blacklistedPairs ?? [] }))
              return
            }
            try {
              const result = await saveConfig({ blacklistedPairs: newPairs })
              const finalPairs = result?.blacklistedPairs || newPairs

              // Передаем в стор объект, а не функцию
              setCfg(prev => ({ ...prev, blacklistedPairs: finalPairs }))
              setBotsConfig({ ...storedConfig, blacklistedPairs: finalPairs })
            } catch (err) {
              console.error('Auto-save error:', err)
              // В случае ошибки возвращаем предыдущий список
              setCfg(prev => ({ ...prev, blacklistedPairs: storedConfig.blacklistedPairs ?? [] }))
              const tg = window.Telegram?.WebApp
              if (tg?.showAlert) tg.showAlert(t.blacklistSaveError)
            }
          }}
        />
      </div>

      {/* Save */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={handleButtonClick}
          disabled={saving}
          style={{
            width: '100%', padding: '16px', borderRadius: 14,
            fontSize: 16, fontWeight: 700,
            background: !hasApiKeysState
              ? 'rgba(255,255,255,0.06)'
              : saved
                ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: !hasApiKeysState ? '#636366' : '#fff',
            border: 'none', cursor: 'pointer',
            transition: 'background 0.3s',
            WebkitTapHighlightColor: 'transparent',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? '...' : (saved ? t.cfgSaved : t.save)}
        </button>

        {!hasApiKeysState && showTooltip && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
            transform: 'translateX(-50%)',
            background: '#1C1C1E', border: '1px solid rgba(255,107,53,0.25)',
            borderRadius: 10, padding: '10px 14px', zIndex: 100,
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            width: 260, textAlign: 'center',
          }}>
            <p style={{ fontSize: 12, color: '#8E8E93', margin: 0, lineHeight: 1.5 }}>
              {t.noApiKeysHint}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function BotsPage() {
  const t              = useT()
  const botsMainTab    = useDashboardStore(s => s.botsMainTab)
  const setBotsMainTab = useDashboardStore(s => s.setBotsMainTab)
  const haptic         = window.Telegram?.WebApp?.HapticFeedback
  const subscriptionExpired = useDashboardStore(s => s.subscriptionExpired)
  const isAdmin             = useDashboardStore(s => s.isAdmin)

  const MAIN_TABS = [
    { id: 'activeBots', label: t.botsTabActiveBots },
    { id: 'config',     label: t.botsTabConfig },
  ]

  // Вкладка «Сигналы» убрана — если у пользователя сохранён выбор 'signals',
  // возвращаемся к «Активные боты», иначе контент был бы пустым.
  const activeTab = (botsMainTab === 'activeBots' || botsMainTab === 'config') ? botsMainTab : 'activeBots'

  return (
    <>
      {/* Tab switcher */}
      <div style={{
        display: 'flex', background: '#1A1A1A',
        borderRadius: 14, padding: 4, gap: 2, marginBottom: 18,
      }}>
        {MAIN_TABS.map(tab => {
          const active = activeTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => { setBotsMainTab(tab.id); haptic?.impactOccurred('light') }}
              style={{
                flex: 1, padding: '10px 6px', borderRadius: 10,
                fontSize: 12, fontWeight: 700,
                background: active
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                  : 'transparent',
                color: active ? '#fff' : '#636366',
                border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Баннер: подписка истекла — боты остановлены. Ползунки на карточках
          заблокированы (см. BotCard: subLocked). */}
      {activeTab === 'activeBots' && subscriptionExpired && !isAdmin && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(255,59,48,0.10)', border: '1px solid rgba(255,59,48,0.25)',
          borderRadius: 14, padding: '10px 12px', marginBottom: 12,
        }}>
          <span style={{ fontSize: 16 }}>⛔</span>
          <span style={{ fontSize: 12.5, color: '#FF6B6B', fontWeight: 600, lineHeight: 1.4, flex: 1 }}>
            {t.botSubExpiredBanner}
          </span>
        </div>
      )}

      {activeTab === 'activeBots' && <ActiveBotsTab t={t} />}
      {activeTab === 'config'     && <ConfigTab t={t} />}
    </>
  )
}